/**
 * Contracts for module "team". Owner: partners.
 * Customer team members, invites, the role/permission matrix, support-access consent and agency staff.
 * Import as `@qm/contracts/team`.
 */
import { z } from 'zod';

export const TEAM_ROLES = ['owner', 'admin', 'approver', 'member', 'billing', 'viewer'] as const;
export const TeamRoleSchema = z.enum(TEAM_ROLES);
export type TeamRole = z.infer<typeof TeamRoleSchema>;
/** Roles an owner can invite (owners are made by promotion, not invite, except for agency-created workspaces). */
export const InvitableRoleSchema = z.enum(['admin', 'approver', 'member', 'billing', 'viewer']);

/** Permission vocabulary shown in the matrix (QM-A12-002): read-only, read-write, approval, billing, team management. */
export const PERMISSIONS = ['read', 'write', 'approval', 'billing', 'manage_team'] as const;
export const PermissionSchema = z.enum(PERMISSIONS);
export type Permission = z.infer<typeof PermissionSchema>;

export const DEFAULT_ROLE_PERMISSIONS: Record<TeamRole, readonly Permission[]> = {
  owner: ['read', 'write', 'approval', 'billing', 'manage_team'],
  admin: ['read', 'write', 'approval', 'manage_team'],
  approver: ['read', 'approval'],
  member: ['read', 'write'],
  billing: ['read', 'billing'],
  viewer: ['read'],
};

/** identity_memberships.role written for each team role (sign-in membership). */
export const IDENTITY_ROLE_FOR_TEAM_ROLE: Record<TeamRole, 'customer_owner' | 'customer_admin' | 'customer_member' | 'customer_viewer'> = {
  owner: 'customer_owner', admin: 'customer_admin', approver: 'customer_member', member: 'customer_member', billing: 'customer_member', viewer: 'customer_viewer',
};

export const INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** Seats included when staff have not set a limit (ops_tenant_limits.seats). */
export const DEFAULT_SEAT_LIMIT = 5;

export const InviteCreateSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  role: InvitableRoleSchema,
});
export const InviteAcceptSchema = z.object({
  token: z.string().min(20).max(200),
  /** only used when the caller is not signed in; a signed-in caller's own email always wins */
  email: z.string().trim().toLowerCase().email().optional(),
});
export const MemberRoleUpdateSchema = z.object({ role: TeamRoleSchema });
export const MemberPermissionsUpdateSchema = z.object({ extraPermissions: z.array(PermissionSchema).max(PERMISSIONS.length) });
export const RolePermissionsUpdateSchema = z.object({ permissions: z.array(PermissionSchema).max(PERMISSIONS.length) });
export const SuspendSchema = z.object({ reason: z.string().trim().min(5).max(500) });

export const SupportConsentSchema = z.object({
  durationMinutes: z.number().int().min(5).max(240).default(30),
  note: z.string().trim().max(300).optional(),
});

export const AGENCY_ROLES = ['admin', 'account_manager', 'creative'] as const;
export const AgencyRoleSchema = z.enum(AGENCY_ROLES);
export const AgencyInviteSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  agencyRole: AgencyRoleSchema,
  clientTenantIds: z.array(z.string().min(1).max(64)).max(500).default([]),
});
export const AgencyInviteAcceptSchema = z.object({ token: z.string().min(20).max(200), displayName: z.string().trim().max(120).optional() });
export const AssignmentsUpdateSchema = z.object({ clientTenantIds: z.array(z.string().min(1).max(64)).max(500) });

/** Canonical invite link (QM-A12-004). Legacy `/join?token=` links are redirected here by the portal. */
export const canonicalInvitePath = (token: string) => `/invite#token=${encodeURIComponent(token)}`;
export const LEGACY_INVITE_PATHS = ['/join', '/accept-invite'] as const;

export const TEAM_EVENTS = {
  inviteEmail: 'team.invite.email',
  agencyInviteEmail: 'team.agency_invite.email',
  memberSuspended: 'team.member.suspended',
} as const;
